/**
 * 내비게이션 단일 소스
 *
 * 데스크톱 상단 메뉴(Navigation), 모바일 드로어(MobileNavigation),
 * 모바일 하단탭(MobileBottomNavigation) 이 모두 이 정의를 읽는다.
 * 라벨/경로/아이콘을 바꿀 때는 여기만 수정한다.
 *
 * group
 *  - 'primary': 데스크톱 상단 메뉴에 직접 노출
 *  - 'more'   : 데스크톱 "더보기" 메뉴로 이동
 * collapseBelowLg
 *  - true 이면 md~lg(좁은 데스크톱) 폭에서는 주 메뉴에서 빠지고 "더보기" 로 넘어간다.
 * bottomTab
 *  - true 이면 모바일 하단탭에 직접 노출 (나머지는 "더보기" 드로어)
 */
import {
  AccountBalance,
  Assessment,
  Event,
  Home,
  Paid,
  Settings,
  ShowChart,
  SwapHoriz,
  Timeline,
  TrendingUp,
} from '@mui/icons-material';

/** 로그인 직후 첫 화면 / 로고 클릭 / index 라우트 이동처 */
export const HOME_PATH = '/home';

export const NAV_ITEMS = [
  {
    key: 'home',
    path: HOME_PATH,
    label: '홈',
    shortLabel: '홈',
    Icon: Home,
    group: 'primary',
    bottomTab: true,
  },
  {
    key: 'portfolio',
    path: '/portfolio',
    label: '포트폴리오',
    shortLabel: '포트폴리오',
    Icon: TrendingUp,
    group: 'primary',
    bottomTab: true,
  },
  {
    key: 'accounts',
    path: '/accounts',
    label: '계좌',
    shortLabel: '계좌',
    Icon: AccountBalance,
    group: 'primary',
    bottomTab: true,
  },
  {
    key: 'transactions',
    path: '/transactions',
    label: '거래내역',
    shortLabel: '거래내역',
    Icon: SwapHoriz,
    group: 'primary',
  },
  {
    key: 'dividends',
    path: '/dividends',
    label: '배당',
    shortLabel: '배당',
    Icon: Paid,
    group: 'primary',
    collapseBelowLg: true,
  },
  {
    key: 'ipo',
    path: '/ipo',
    label: '공모주',
    shortLabel: '공모주',
    Icon: Event,
    group: 'primary',
    collapseBelowLg: true,
    bottomTab: true,
  },
  {
    key: 'ai-analysis',
    path: '/ai-analysis',
    label: 'AI 분석',
    shortLabel: 'AI 분석',
    Icon: Assessment,
    group: 'primary',
    collapseBelowLg: true,
  },
  {
    key: 'simulation',
    path: '/simulation',
    label: '시뮬레이션',
    shortLabel: '시뮬레이션',
    Icon: Timeline,
    group: 'more',
  },
  {
    key: 'strategy-investment',
    path: '/strategy-investment',
    label: '전략투자',
    shortLabel: '전략투자',
    Icon: ShowChart,
    group: 'more',
  },
  {
    key: 'settings',
    path: '/settings',
    label: '설정',
    shortLabel: '설정',
    Icon: Settings,
    group: 'more',
  },
];

/** "더보기" 항목(데스크톱 메뉴 버튼 / 모바일 하단탭) 공통 라벨 */
export const MORE_LABEL = '더보기';
export const MORE_KEY = 'more';

export const PRIMARY_NAV_ITEMS = NAV_ITEMS.filter((item) => item.group === 'primary');
export const MORE_NAV_ITEMS = NAV_ITEMS.filter((item) => item.group === 'more');
export const BOTTOM_NAV_ITEMS = NAV_ITEMS.filter((item) => item.bottomTab);

/**
 * pathname 이 항목 경로에 속하는지 (정확 일치 또는 하위 경로).
 * '/ipo-foo' 같은 접두 문자열 오탐을 막기 위해 경로 구분자 경계에서만 매칭한다.
 */
export const isPathActive = (itemPath, pathname) => {
  if (!pathname) return false;
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
};

/** 현재 경로에 해당하는 내비 항목 (없으면 null → 선택 표시 없음) */
export const getActiveNavItem = (pathname) =>
  NAV_ITEMS.find((item) => isPathActive(item.path, pathname)) || null;

/**
 * 전역 "거래 기록" 확장 FAB (모바일 전용)
 *
 * - 홈·포트폴리오·계좌·거래내역·배당 화면에서만 노출한다.
 *   (시뮬레이션은 자체 FAB 과 겹치고, 전략투자·설정·AI·공모주는 거래 기록과 맥락이 멀다)
 * - 누르면 /transactions 로 이동하면서 라우터 state 로 거래 추가 다이얼로그를 연다.
 *   Transactions 페이지가 state 를 읽어 다이얼로그를 열고 곧바로 지운다(새로고침·뒤로가기 재오픈 방지).
 */
export const TRANSACTION_FAB_PATHS = ['/home', '/portfolio', '/accounts', '/transactions', '/dividends'];
export const TRANSACTION_FAB_LABEL = '거래 기록';
export const TRANSACTION_FAB_TARGET_PATH = '/transactions';
export const OPEN_ADD_TRANSACTION_STATE_KEY = 'openAddTransaction';

/** 현재 경로에서 전역 "거래 기록" FAB 를 보여줄지 */
export const isTransactionFabVisible = (pathname) =>
  TRANSACTION_FAB_PATHS.some((path) => isPathActive(path, pathname));
